import React, { useState, useEffect, useCallback, useContext } from 'react'
import styled, { useTheme } from 'styled-components'
import _map from 'lodash.map'
import { FaMinusCircle, FaTimes } from 'react-icons/fa'
import Select from 'react-select'

import { axios, context as AuthContext } from '../../context/auth'
import { isOwner } from '../../utils/auth'
import { CATEGORIES, ACTIONS, event } from '../../utils/analytics-tracker'
import { PrimaryButton } from '../../styled/button'
import LinkButton from '../../styled/link-button'
import InviteUserModal from '../invitations/invite-user-modal'
import { getSelectStyles } from '../../styled/select'
import { ConfirmFocal } from '../../styled/confirm-modal'
import confirm from '../confirmable'

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.bodyText};
  border-radius: 8px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid ${({ theme }) => theme.borderColor};
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
`

const ModalTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.bodyText};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${({ theme }) => theme.bodyText};
  padding: 0;
  
  &:hover {
    opacity: 0.7;
  }
`

const Section = styled.div`
  margin-bottom: 2rem;
`

const SectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: ${({ theme }) => theme.bodyText};
  font-size: 1.1rem;
`

const PermissionRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 4px;
  margin-bottom: 0.5rem;
  gap: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`

const PermissionName = styled.div`
  flex: 2;
  font-weight: 500;
  color: ${({ theme }) => theme.bodyText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PermissionRole = styled.div`
  flex: 1;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.secondaryText};
  text-align: center;
`

const PermissionRemove = styled.div`
  flex: 0 0 auto;
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const GroupSelector = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const SelectGroup = styled.div`
  flex: 1;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: ${({ theme }) => theme.bodyText};
  }
`

const SelectRole = styled.div`
  flex: 1;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: ${({ theme }) => theme.bodyText};
  }
`

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.secondaryText};
`

const EmptyMessage = styled.div`
  text-align: center;
  padding: 1rem;
  color: ${({ theme }) => theme.secondaryText};
  font-style: italic;
`

const PendingInvitationRow = styled(PermissionRow)`
  opacity: 0.7;
`

const canRemove = (objUser, authUser, isOwner, users) => {
  // Safety check - if no auth user or no objUser, can't remove
  if (!authUser || !objUser) {
    return false
  }

  const isSelf = objUser._id === authUser._id

  // Owners can remove anyone except themselves
  if (isOwner && !isSelf) {
    return true
  }

  // Users can remove themselves unless they're the sole owner
  if (isSelf) {
    const ownerCount = users.filter(u => u.role === 'OWNER').length
    const userIsOwner = users.some(u => u._id === authUser._id && u.role === 'OWNER')

    // Can't remove yourself if you're the only owner
    if (userIsOwner && ownerCount === 1) {
      return false
    }

    return true
  }

  return false
}

const PermissionsModal = ({ isOpen, onClose, contentType, contentId, contentName }) => {
  const { user } = useContext(AuthContext)
  const theme = useTheme()
  const [permObject, setPermObject] = useState({})
  const [userGroups, setUserGroups] = useState([])
  const [owner, setOwner] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [selectedRole, setSelectedRole] = useState(null)

  // Use shared select styles from styled/select.js
  const selectStyles = getSelectStyles(theme)

  // Get API path and roles based on content type
  const getApiPath = () => {
    switch (contentType) {
      case 'song': return 'songs'
      case 'setlist': return 'setlists'
      case 'group': return 'groups'
      default: return 'songs'
    }
  }

  const getRoles = () => {
    if (contentType === 'group') {
      return ['MEMBER', 'OWNER']
    }
    return ['VIEWER', 'EDITOR', 'OWNER']
  }

  const getCategory = () => {
    switch (contentType) {
      case 'song': return CATEGORIES.SONG
      case 'setlist': return CATEGORIES.SETLIST
      case 'group': return CATEGORIES.GROUP
      default: return CATEGORIES.SONG
    }
  }

  const fetchPermissions = useCallback(async () => {
    if (!contentId) return

    try {
      setLoading(true)
      const response = await axios({
        url: `/api/${getApiPath()}/${contentId}`,
        method: 'GET'
      })
      setPermObject(response.data || {})
      setOwner(isOwner(response.data))
    } catch (error) {
      console.error('Error fetching permissions:', error)
    } finally {
      setLoading(false)
    }
  }, [contentId, contentType])

  const fetchUserGroups = useCallback(async () => {
    if (!user) return

    try {
      // Always fetch groups from API to ensure we have the latest data
      const response = await axios({
        url: '/api/groups',
        method: 'GET'
      })
      console.log('Fetched user groups:', response.data)
      setUserGroups(response.data || [])
    } catch (error) {
      console.error('Error fetching user groups:', error)
      setUserGroups([])
    }
  }, [user])

  useEffect(() => {
    if (isOpen) {
      console.log('Modal opened, fetching data...')
      fetchPermissions()
      fetchUserGroups()
      // Set default role for react-select
      const roles = getRoles()
      setSelectedRole({ value: roles[0], label: roles[0] })
    }
  }, [isOpen, fetchPermissions, fetchUserGroups])

  const addGroup = useCallback(async (groupId, role) => {
    try {
      await axios({
        url: `/api/${getApiPath()}/${contentId}/groups`,
        method: 'POST',
        data: {
          groupid: groupId,
          role
        }
      })
      event(getCategory(), ACTIONS.GROUP_ADDED)
      fetchPermissions()
      setSelectedGroup(null)
    } catch (error) {
      console.error('Error adding group:', error)
    }
  }, [contentId, contentType, fetchPermissions])

  const removeUser = useCallback(async (userToRemove) => {
    const isSelfRemoval = userToRemove._id === user._id

    // Create confirmation message
    const confirmationMessage = isSelfRemoval
      ? (
        <div>
          Are you sure you want to remove yourself from
          <ConfirmFocal>{contentName}</ConfirmFocal>
          ? You will lose access to this {contentType}.
        </div>
        )
      : (
        <div>
          Are you sure you want to remove
          <ConfirmFocal>{userToRemove.email}</ConfirmFocal>
          from
          <ConfirmFocal>{contentName}</ConfirmFocal>
          ?
        </div>
        )

    try {
      await confirm(confirmationMessage)

      await axios({
        url: `/api/${getApiPath()}/${contentId}/users/${userToRemove._id}`,
        method: 'DELETE'
      })
      event(getCategory(), ACTIONS.USER_REMOVED)

      if (isSelfRemoval) {
        // Show confirmation and redirect
        window.alert(`You have been removed from "${contentName}"`)
        onClose()

        // Redirect to appropriate list
        if (contentType === 'song') {
          window.location.href = '/songs'
        } else if (contentType === 'setlist') {
          window.location.href = '/setlists'
        } else if (contentType === 'group') {
          window.location.href = '/groups'
        }
      } else {
        fetchPermissions()
      }
    } catch (error) {
      // User cancelled or error occurred
      if (error.message !== 'User cancelled') {
        console.error('Error removing user:', error)
      }
    }
  }, [contentId, contentType, contentName, user, onClose, fetchPermissions])

  const removeGroup = useCallback(async (groupToRemove) => {
    // Create confirmation message
    const confirmationMessage = (
      <div>
        Are you sure you want to remove group
        <ConfirmFocal>{groupToRemove._doc.title}</ConfirmFocal>
        from
        <ConfirmFocal>{contentName}</ConfirmFocal>
        ?
      </div>
    )

    try {
      await confirm(confirmationMessage)

      await axios({
        url: `/api/${getApiPath()}/${contentId}/groups/${groupToRemove._doc._id}`,
        method: 'DELETE'
      })
      event(getCategory(), ACTIONS.GROUP_REMOVED)
      fetchPermissions()
    } catch (error) {
      // User cancelled or error occurred
      if (error.message !== 'User cancelled') {
        console.error('Error removing group:', error)
      }
    }
  }, [contentId, contentType, contentName, fetchPermissions])

  const cancelInvitation = useCallback(async (invitationId, invitationEmail) => {
    // Create confirmation message
    const confirmationMessage = (
      <div>
        Are you sure you want to cancel the invitation for
        <ConfirmFocal>{invitationEmail}</ConfirmFocal>
        ?
      </div>
    )

    try {
      await confirm(confirmationMessage)

      await axios({
        url: `/api/invitations/${invitationId}`,
        method: 'DELETE'
      })
      fetchPermissions()
    } catch (error) {
      // User cancelled or error occurred
      if (error.message !== 'User cancelled') {
        console.error('Error canceling invitation:', error)
      }
    }
  }, [fetchPermissions])

  const handleAddGroup = () => {
    if (selectedGroup && selectedRole) {
      addGroup(selectedGroup.value, selectedRole.value)
      setSelectedGroup(null)
    }
  }

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  const { users = [], groups = [], pendingInvitations = [] } = permObject
  const roles = getRoles()

  return (
    <>
      <ModalOverlay onClick={handleOverlayClick}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Manage Permissions<br />{contentName}</ModalTitle>
            <CloseButton onClick={onClose}>
              <FaTimes />
            </CloseButton>
          </ModalHeader>

          {loading
            ? (
              <LoadingMessage>Loading permissions...</LoadingMessage>
              )
            : (
              <>
                {/* Users Section */}
                <Section>
                  {users.length === 0
                    ? <EmptyMessage>No users have access</EmptyMessage>
                    : _map(users, objUser => (
                      <PermissionRow key={objUser._id}>
                        <PermissionName>{objUser.email}</PermissionName>
                        <PermissionRole>{objUser.role}</PermissionRole>
                        <PermissionRemove>
                          {canRemove(objUser, user, owner, users) && (
                            <LinkButton onClick={() => removeUser(objUser)}>
                              <FaMinusCircle />
                            </LinkButton>
                          )}
                        </PermissionRemove>
                      </PermissionRow>
                    ))}
                  {groups.length === 0
                    ? <EmptyMessage>No groups have access</EmptyMessage>
                    : _map(groups, (objGroup, index) => (
                      <PermissionRow key={`${objGroup._doc._id}-${index}`}>
                        <PermissionName>{objGroup._doc.title}</PermissionName>
                        <PermissionRole>{objGroup.role}</PermissionRole>
                        <PermissionRemove>
                          {owner && (
                            <LinkButton onClick={() => removeGroup(objGroup)}>
                              <FaMinusCircle />
                            </LinkButton>
                          )}
                        </PermissionRemove>
                      </PermissionRow>
                    ))}
                </Section>

                {/* Pending Invitations */}
                {pendingInvitations.length > 0 && (
                  <Section>
                    <SectionTitle>Pending Invitations</SectionTitle>
                    {_map(pendingInvitations, invitation => (
                      <PendingInvitationRow key={invitation._id}>
                        <PermissionName>{invitation.email}</PermissionName>
                        <PermissionRole>{invitation.permissionLevel} (Pending)</PermissionRole>
                        <PermissionRemove>
                          {owner && (
                            <LinkButton onClick={() => cancelInvitation(invitation._id, invitation.email)}>
                              <FaMinusCircle />
                            </LinkButton>
                          )}
                        </PermissionRemove>
                      </PendingInvitationRow>
                    ))}
                  </Section>
                )}

                {/* Owner Actions */}
                {owner && (
                  <Section>
                    <SectionTitle>Add Access</SectionTitle>

                    {/* Invite User Button */}
                    <PrimaryButton
                      onClick={() => setShowInviteModal(true)}
                      style={{ marginBottom: '1rem', display: 'block' }}
                    >
                      Invite User by Email
                    </PrimaryButton>

                    {/* Group Selection - Hide for group content */}
                    {contentType !== 'group' && (
                      <>
                        <SectionTitle style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Add Group</SectionTitle>
                        {console.log('userGroups:', userGroups, 'length:', userGroups.length)}
                        {userGroups.length === 0
                          ? <EmptyMessage>You don't belong to any groups</EmptyMessage>
                          : (
                            <>
                              <GroupSelector>
                                <SelectGroup>
                                  <label>Select Group</label>
                                  <Select
                                    value={selectedGroup}
                                    onChange={setSelectedGroup}
                                    options={userGroups.map(group => ({
                                      value: group._id,
                                      label: group.title
                                    }))}
                                    styles={selectStyles}
                                    placeholder='-- Select a group --'
                                    isClearable
                                  />
                                </SelectGroup>
                                <SelectRole>
                                  <label>Role</label>
                                  <Select
                                    value={selectedRole}
                                    onChange={setSelectedRole}
                                    options={roles.map(role => ({
                                      value: role,
                                      label: role
                                    }))}
                                    styles={selectStyles}
                                  />
                                </SelectRole>
                              </GroupSelector>
                              <PrimaryButton
                                onClick={handleAddGroup}
                                disabled={!selectedGroup}
                              >
                                Add Group
                              </PrimaryButton>
                            </>
                            )}
                      </>
                    )}
                  </Section>
                )}
              </>
              )}
        </ModalContent>
      </ModalOverlay>

      {/* Invite User Modal */}
      {showInviteModal && (
        <InviteUserModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          contentType={contentType}
          contentId={contentId}
          onSuccess={() => {
            setShowInviteModal(false)
            fetchPermissions()
          }}
        />
      )}
    </>
  )
}

export default PermissionsModal
